/**
 * Générateur — Il résout des problèmes s'y ramenant, internes aux
 * mathématiques ou en lien avec d'autres disciplines.
 * Attendu ID : 212 | Niveau : 3ème | Domaine : Calcul littéral (d12, O12)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séances A7S4 + A8S3). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « brevet »    — l'architecture brevet : développer P, factoriser Q,
 *                   constater P = Q (cohérence par construction)
 *   « programme » — programme de calcul littéral (factoriser pour conclure)
 *   « equation »  — mises en équation (timbres / fraction / roues / rectangle)
 *   « pythagore » — Pythagore mène à une équation (le x² qui disparaît)
 *   « geometrie » — aire du carré = k × aire du triangle (équation)
 *   « surprise »  — aires égales… ou pas (solution négative → impossible, QCM)
 *
 * Adaptation : les schémas des exercices deviennent des descriptions texte
 * (« ABC rectangle en A… ») — figures non nécessaires à la résolution.
 */

import {
    randomChoice, randInt, tex, fracTex,
    makeQcm, makeTexteTrou,
} from '../../../utils';
import { carre } from './litteral';

const ATTENDU_ID = 212;

const L_BREVET = 'Développer, factoriser, relier (architecture brevet)';
const L_PROGRAMME = 'Étudier un programme de calcul littéral';
const L_EQUATION = 'Mettre un problème en équation';
const L_PYTHAGORE = 'Résoudre avec Pythagore et une équation';
const L_GEOMETRIE = 'Comparer des aires avec une équation';
const L_SURPRISE = 'Discuter une équation issue d\'un problème';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « brevet » — P développé = Q factorisé ═════════

const genBrevetTrous = () => {
    // Q = (x + c)² − k² = (x + c + k)(x + c − k) = P avec a = c + k, b = c − k.
    let c; let k; let a; let b;
    for (let essai = 0; essai < 60; essai++) {
        c = randInt(2, 12); k = randInt(2, Math.min(9, c + 3));
        a = c + k; b = c - k;
        if (b > 0 && a !== b) break;
        b = 0;
    }
    if (!b) { c = 7; k = 5; a = 12; b = 2; }              // repli (doc A7S4.Ex.2)
    const S = a + b; const Pconst = a * b;
    const phrase = `$P = ${carre()} + {{${S}}}x + {{${Pconst}}} \\qquad Q = \\left(x + {{${a}}}\\right)\\left(x + {{${b}}}\\right)$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`P = \\left(x + ${a}\\right)\\left(x + ${b}\\right)`)} et `
        + `${tex(`Q = \\left(x + ${c}\\right)^{2} - ${k * k}`)}.</p>`
        + `<p><b>Développe et réduis P, puis factorise Q</b> avec ${tex('a^{2} - b^{2}')}.</p>`,
        { ...opts(L_BREVET), mode: 'saisie' });
    payload._meta.params = { c, k, a, b };
    return payload;
};

// ═════════ « programme » — factoriser pour conclure ═════════

const PROGRAMMES = [
    {
        etapes: (n) => `choisis un entier n ; mets-le au carré ; prends le double du résultat ; soustrais le produit de n par l'entier qui le suit (n + 1)`,
        exprTrous: ['2', '1'],                             // 2n² − n(n + {{1}})
        phraseExpr: (t1, t2) => `E = {{${t1}}}n^{2} - n\\left(n + {{${t2}}}\\right)`,
        facteur: '1',                                      // E = n(n − {{1}})
        phraseFact: (f) => `E = n\\left(n - {{${f}}}\\right)`,
        conclusion: 'Multiplier le nombre de départ par le nombre qui le PRÉCÈDE.',
        distracteurs: ['Multiplier le nombre de départ par le nombre qui le SUIT.', 'Élever le nombre de départ au carré.'],
    },
    {
        etapes: () => 'choisis un entier n ; mets-le au carré ; prends le double du résultat ; soustrais le produit de n par l\'entier qui le précède (n − 1)',
        exprTrous: ['2', '1'],
        phraseExpr: (t1, t2) => `E = {{${t1}}}n^{2} - n\\left(n - {{${t2}}}\\right)`,
        facteur: '1',
        phraseFact: (f) => `E = n\\left(n + {{${f}}}\\right)`,
        conclusion: 'Multiplier le nombre de départ par le nombre qui le SUIT.',
        distracteurs: ['Multiplier le nombre de départ par le nombre qui le PRÉCÈDE.', 'Élever le nombre de départ au carré.'],
    },
    {
        etapes: () => 'choisis un entier n ; mets-le au carré ; prends le triple du résultat ; soustrais le produit de n par (3n − 2)',
        exprTrous: ['3', '2'],
        phraseExpr: (t1, t2) => `E = {{${t1}}}n^{2} - n\\left(3n - {{${t2}}}\\right)`,
        facteur: '2',
        phraseFact: (f) => `E = {{${f}}}n`,
        conclusion: 'DOUBLER le nombre de départ.',
        distracteurs: ['Élever le nombre de départ au carré.', 'Multiplier le nombre de départ par le nombre qui le SUIT.'],
    },
];

const PRENOMS_PROG = ['Léa', 'Sami', 'Nora', 'Tom', 'Aya', 'Marius', 'Lise', 'Ibrahim'];

const genProgrammeTrous = () => {
    const prog = randomChoice(PROGRAMMES);
    const prenom = randomChoice(PRENOMS_PROG);
    // Deux lignes ENTIÈREMENT mathématiques (jamais un mélange texte/math avec
    // des trous — cf. FORMATS_TANOED §0.5bis).
    const phrase = `$${prog.phraseExpr(prog.exprTrous[0], prog.exprTrous[1])}$\n`
        + `$${prog.phraseFact(prog.facteur)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${prenom} propose ce programme de calcul : ${prog.etapes()}.</p>`
        + '<p><b>Écris l\'expression du résultat E, puis sa forme factorisée et réduite.</b></p>',
        { ...opts(L_PROGRAMME), mode: 'saisie' });
    payload._meta.params = { conclusion: prog.conclusion, prenom };
    return payload;
};

// QCM conclusion du programme (séparé pour rester mono-famille par format).
const genProgrammeQcm = () => {
    const prog = randomChoice(PROGRAMMES);
    const prenom = randomChoice(PRENOMS_PROG);
    const payload = makeQcm([prog.conclusion, ...prog.distracteurs], prog.conclusion,
        // « Après factorisation, le résultat se réduit fortement » annonçait la
        // méthode ET la conclusion : c'est ce que l'exercice demande de trouver.
        `<p>${prenom} propose ce programme de calcul : ${prog.etapes()}.</p>`
        + '<p><b>À quoi ce programme revient-il ?</b></p>',
        opts(L_PROGRAMME));
    payload._meta.params = { conclusion: prog.conclusion, prenom };
    return payload;
};

// ═════════ « equation » — mises en équation (rotation de familles) ═════════

const genEquationTrous = () => {
    const famille = randomChoice(['timbres', 'fraction', 'roues', 'rectangle']);
    if (famille === 'timbres') {
        // N + P = T ; P + t = 2(N − t) → N = T/3 + t, P = T − N.
        let t; let base; let nath; let pierre;
        do {
            t = randInt(2, 6);
            base = randInt(12, 40) * 3;                    // T multiple de 3
            nath = base / 3 + t;
            pierre = base - nath;
        } while (nath === pierre);
        const [p1, p2] = randomChoice([['Pierre', 'Nathalie'], ['Louis', 'Emma'], ['Hugo', 'Chloé']]);
        const phrase = `${p2} possède {{${nath}}} timbres et ${p1} en possède {{${pierre}}}.`;
        const payload = makeTexteTrou(phrase,
            `<p>${p1} et ${p2} ont ${base} timbres à eux deux. Si ${p2} donnait ${t} timbres à ${p1}, `
            + `il en aurait exactement deux fois plus qu'elle.</p>`
            + '<p><b>Mets en équation et résous.</b></p>',
            { ...opts(L_EQUATION), mode: 'saisie' });
        payload._meta.params = { famille, base, t, nath, pierre };
        return payload;
    }
    if (famille === 'fraction') {
        // (a + x)/(b + x) = c/d, construit depuis x cible (peut être négatif).
        let a; let b; let c; let d; let x;
        for (let essai = 0; essai < 60; essai++) {
            [c, d] = randomChoice([[2, 3], [3, 4], [1, 2], [3, 5], [2, 5]]);
            const m = randInt(2, 9);
            x = randomChoice([-4, -3, -2, -1, 1, 2, 3, 4, 5]);
            a = c * m - x; b = d * m - x;
            if (a >= 1 && b >= 2 && a < b) break;
            a = 0;
        }
        if (!a) { a = 4; b = 5; c = 2; d = 3; x = -2; }    // repli (doc Ex.2)
        const phrase = `Le nombre cherché est {{${x}}}.`;
        const payload = makeTexteTrou(phrase,
            `<p>On ajoute un même nombre au numérateur et au dénominateur de ${fracTex(a, b)}, `
            + `et on obtient une fraction égale à ${fracTex(c, d)}.</p>`
            + '<p><b>Quel est ce nombre ?</b> Il peut être négatif : écris-le alors avec son signe.</p>',
            { ...opts(L_EQUATION), mode: 'saisie' });
        payload._meta.params = { famille, a, b, c, d, x };
        return payload;
    }
    if (famille === 'roues') {
        // 2m + 4(v − m) = r → m = (4v − r)/2.
        let v; let m;
        v = randInt(40, 90);
        m = randInt(10, v - 10);
        const r = 2 * m + 4 * (v - m);
        const phrase = `Il y a {{${m}}} motos.`;
        const payload = makeTexteTrou(phrase,
            `<p>Un parking compte ${v} véhicules — des motos à 2 roues et des voitures `
            + `à 4 roues —, soit ${r} roues en tout.</p>`
            + '<p><b>Combien y a-t-il de motos ?</b></p>',
            { ...opts(L_EQUATION), mode: 'saisie' });
        payload._meta.params = { famille, v, r, m };
        return payload;
    }
    // rectangle : L + l = P/2 ; k1·L et k2·l → nouveau périmètre = P + Δ.
    let L; let l;
    do { L = randInt(7, 15); l = randInt(4, 12); } while (l >= L);
    const P = 2 * (L + l);
    const delta = (6 * L + 4 * l) - P;                     // longueur triplée, largeur doublée
    const phrase = `Longueur : {{${L}}} cm ; largeur : {{${l}}} cm.`;
    const payload = makeTexteTrou(phrase,
        `<p>Le périmètre d'un rectangle est ${P} cm. Si on triple sa longueur et qu'on double `
        + `sa largeur, le périmètre augmente de ${delta} cm.</p>`
        + '<p><b>Quelles sont les dimensions du rectangle ?</b></p>',
        { ...opts(L_EQUATION), mode: 'saisie' });
    payload._meta.params = { famille, L, l, P, delta };
    return payload;
};

// ═════════ « pythagore » — le x² qui disparaît ═════════

const genPythagoreTrous = () => {
    // (x + d)² = x² + c² → 2dx + d² = c² → x = (c² − d²)/(2d), choisi ENTIER
    // ou demi-entier propre.
    let c; let d; let x2;                                  // x2 = 2x entier
    for (let essai = 0; essai < 80; essai++) {
        d = randInt(1, 4); c = randInt(d + 2, 14);
        const num = c * c - d * d;
        if (num % d !== 0) continue;
        x2 = num / d;                                      // 2x
        if (x2 >= 2 && x2 <= 40) break;
        x2 = 0;
    }
    if (!x2) { c = 6; d = 3; x2 = 9; }                     // repli (doc : x = 4,5)
    const xAff = x2 % 2 === 0 ? String(x2 / 2) : `${(x2 - 1) / 2},5`;
    const objet = randomChoice(['Un triangle rectangle', 'Le triangle rectangle d\'une rampe',
        'Le triangle rectangle formé par une échelle contre un mur', 'Le triangle rectangle d\'une équerre géante']);
    const phrase = `x = {{${xAff}}}`;
    const payload = makeTexteTrou(phrase,
        `<p>${objet} a pour côtés de l'angle droit ${c} et x, et pour hypoténuse ${tex(`x + ${d}`)}.</p>`
        // « les x² disparaissent ! » annonçait le ressort de l'exercice.
        + `<p>D'après Pythagore : ${tex(`\\left(x + ${d}\\right)^{2} = ${carre()} + ${c * c}`)}.</p>`
        + '<p><b>Résous et donne x.</b> Utilise une virgule si le résultat n\'est pas entier.</p>',
        { ...opts(L_PYTHAGORE), mode: 'saisie' });
    payload._meta.params = { c, d, xAff };
    return payload;
};

// ═════════ « geometrie » — aire du carré = k × aire du triangle ═════════

const genGeometrieTrous = () => {
    let a; let k; let x;
    for (let essai = 0; essai < 60; essai++) {
        a = randomChoice([4, 6, 8, 10, 12]);
        k = randomChoice([3, 4, 6]);
        if ((2 * a) % k !== 0) continue;
        x = a - (2 * a) / k;
        if (x >= 1 && x < a) break;
        x = 0;
    }
    if (!x) { a = 6; k = 3; x = 2; }                       // repli (doc Ex.7)
    // Noms de sommets variés (variabilité d'habillage, la géométrie est la même).
    const [s1, s2, s3, s4, pt] = randomChoice([
        ['A', 'B', 'C', 'D', 'E'], ['E', 'F', 'G', 'H', 'M'], ['M', 'N', 'P', 'Q', 'T'], ['R', 'S', 'T', 'U', 'K'],
    ]);
    const nomCarre = `${s1}${s2}${s3}${s4}`;
    const nomTri = `${s1}${s4}${pt}`;
    const phrase = `$\\text{Aire de ${nomTri} : } \\dfrac{${a}\\left(${a} - x\\right)}{2}`
        + ` \\qquad \\text{il faut } x = {{${x}}}$`;
    const payload = makeTexteTrou(phrase,
        // `\lbrack` : un crochet brut casserait le parseur `[ligne|colonne]`
        // partout où cet énoncé est réutilisé (FORMATS_TANOED §4).
        `<p>${nomCarre} est un carré de côté ${a}. ${pt} est un point de `
        + `${tex(`\\lbrack ${s1}${s2}\\rbrack`)} tel que ${tex(`${pt}${s2} = x`)} ; `
        + `donc ${tex(`${s1}${pt} = ${a} - x`)}.</p>`
        + `<p>L'aire du triangle ${nomTri} vaut ${tex(`\\dfrac{${s1}${pt} \\times ${s1}${s4}}{2}`)}. `
        + `<b>Pour quelle valeur de x l'aire du carré est-elle égale à ${k} fois celle du `
        + `triangle ${nomTri} ?</b></p>`,
        { ...opts(L_GEOMETRIE), mode: 'saisie' });
    payload._meta.params = { a, k, x, nomCarre };
    return payload;
};

// ═════════ « surprise » — l'équation qui répond « impossible » (QCM) ═════════

const genSurpriseQcm = () => {
    // Aire triangle (base x, hauteur h) = p·x + c ⟺ (h/2)x = px + c
    // → x = c/(h/2 − p) : positif ou négatif selon h.
    const p = randInt(3, 6); const c = randomChoice([8, 12, 16, 20, 24]);
    const possible = randInt(0, 1) === 1;
    let h;
    if (possible) {
        // h/2 − p divise c → x entier positif.
        const diff = randomChoice([1, 2, 4].filter(dd => c % dd === 0));
        h = 2 * (p + diff);
    } else {
        h = 2 * (p - randInt(1, Math.min(2, p - 1)));      // h/2 < p → x négatif
    }
    const x = c / (h / 2 - p);
    const chOk = `Une seule valeur convient : ${tex(`x = ${possible ? x : Math.abs(x)}`)}`;
    // Le « or x est une longueur » désignait la bonne réponse : seul ce choix
    // portait une justification, donc l'élève le repérait sans raisonner.
    const chImp = 'Aucune valeur ne convient';
    const chTous = 'Toutes les valeurs de x conviennent';
    const correct = possible ? chOk : chImp;
    const payload = makeQcm([chOk, chImp, chTous], correct,
        `<p>Un triangle a pour base x et pour hauteur ${h} ; son aire vaut donc ${tex(`${h / 2}x`)}. `
        + `Une autre figure a pour aire ${tex(`${p}x + ${c}`)}.</p>`
        + '<p>Rappel : x est une longueur, donc x est positif.</p>'
        + '<p><b>Peut-on choisir x pour que les deux aires soient égales ?</b></p>',
        opts(L_SURPRISE));
    payload._meta.params = { p, c, h, possible, x };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    brevet_texte_trou_saisie: genBrevetTrous,
    programme_texte_trou_saisie: genProgrammeTrous,
    programme_qcm_unique: genProgrammeQcm,
    equation_texte_trou_saisie: genEquationTrous,
    pythagore_texte_trou_saisie: genPythagoreTrous,
    geometrie_texte_trou_saisie: genGeometrieTrous,
    surprise_qcm_unique: genSurpriseQcm,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'brevet', label: L_BREVET, formatsList: buildFormats('brevet') },
    { id: 'programme', label: L_PROGRAMME, formatsList: buildFormats('programme') },
    { id: 'equation', label: L_EQUATION, formatsList: buildFormats('equation') },
    { id: 'pythagore', label: L_PYTHAGORE, formatsList: buildFormats('pythagore') },
    { id: 'geometrie', label: L_GEOMETRIE, formatsList: buildFormats('geometrie') },
    { id: 'surprise', label: L_SURPRISE, formatsList: buildFormats('surprise') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
